/**
 * Job photos — the mobile job view's own photos plus its read-only check-in
 * photos (`/api/workitems/{guid}/photos`, docs/qr-scan-workflow-requirements.md
 * §9, D15). Bytes are posted raw, like check-in photos; the server re-encodes
 * to JPEG (HEIC accepted) and returns short-lived read links.
 */

import type { JobPhoto, WorkItemPhotosResponse } from '@techgitter/contracts';

import { readApiError as readError } from './readApiError';

const API_BASE_URL = '/api';

export async function fetchWorkItemPhotos(
  guid: string,
  signal?: AbortSignal,
): Promise<WorkItemPhotosResponse> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/photos`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) throw new Error(await readError(response, 'Failed to load photos'));
  return response.json();
}

export async function uploadJobPhoto(guid: string, file: File): Promise<JobPhoto> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/photos`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      // Encoded: fetch throws on a header value outside ISO-8859-1, so a
      // file named "照片.jpg" could never be sent. The API decodes it.
      'X-Filename': encodeURIComponent(file.name),
    },
    body: file,
  });
  if (!response.ok) throw new Error(await readError(response, 'Failed to upload photo'));
  return response.json();
}

export async function deleteJobPhoto(guid: string, photoId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/photos/${photoId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error(await readError(response, 'Failed to remove photo'));
}
